import type { CmpConfig } from "../core/config";
import { resolveBannerTranslation } from "../core/translation-resolve";
import { removeConsentWidget, syncConsentWidget } from "./consent-widget";
import {
  acquirePageLock,
  releasePageLockIfIdle,
  setStrictPageBlock,
  shouldBlockPageUntilConsent,
} from "./page-lock";
import { getStyles } from "./styles";
import { ENTAPRO_MARK_DATA_URI } from "./brand";

export interface BannerOptions {
  reconsentNotice?: string;
  /** When true, page scroll and clicks are blocked until consent. */
  blockPage?: boolean;
}

export function renderBanner(
  config: CmpConfig,
  onAcceptAll: () => void,
  onRejectAll: () => void,
  onManagePreferences: () => void,
  onOptOut?: () => void,
  options?: BannerOptions
): HTMLElement {
  const lang = config.defaultLanguage || "tr";
  const translation = resolveBannerTranslation(config, lang);

  // Inject styles
  const styleEl = document.createElement("style");
  styleEl.id = "cmp-styles";
  styleEl.textContent = getStyles(config.themeJson);
  document.head.appendChild(styleEl);

  // Per-tenant custom CSS (advanced theming) — injected AFTER the defaults so it
  // overrides them. Lets a customer match a bespoke design without forking the SDK.
  const customCss = config.themeJson?.customCss;
  if (typeof customCss === "string" && customCss.trim() && !document.getElementById("cmp-custom-styles")) {
    const customEl = document.createElement("style");
    customEl.id = "cmp-custom-styles";
    customEl.textContent = customCss;
    document.head.appendChild(customEl);
  }

  // Create banner
  const banner = document.createElement("div");
  banner.id = "cmp-banner";
  banner.className = "cmp-banner";
  banner.setAttribute("role", "dialog");
  banner.setAttribute("aria-label", translation.bannerTitle);

  const buttons = (translation.buttonsJson || {}) as Record<string, string | undefined>;

  const isTr = lang === "tr";
  const fallback = {
    rejectAll:          isTr ? "Tümünü Reddet"                    : "Reject All",
    acceptAll:          isTr ? "Tümünü Kabul Et"                   : "Accept All",
    managePreferences:  isTr ? "Çerez Tercihlerimi Yönet"          : "Manage Cookie Preferences",
  };
  const rejectLabel = buttons.rejectAll || buttons.declineText || fallback.rejectAll;
  const acceptLabel = buttons.acceptAll || buttons.acceptText || fallback.acceptAll;
  const manageLabel =
    buttons.managePreferences || buttons.allowSelectionText || fallback.managePreferences;

  const noticeHtml = options?.reconsentNotice
    ? `<div class="cmp-banner__notice">${escapeHtml(options.reconsentNotice)}</div>`
    : "";

  const themeJson = config.themeJson as any || {};
  const compliance = themeJson.compliance || {};
  const isCCPA = compliance.ccpaEnabled === true;

  const doNotSellHtml = isCCPA
    ? `<div class="cmp-banner__do-not-sell-wrap"><a href="#" id="cmp-do-not-sell" class="cmp-banner__do-not-sell-link">Do Not Sell or Share My Personal Information</a></div>`
    : "";

  const logoVisible = themeJson.logoVisible !== false;
  const brandHtml = logoVisible
    ? `<div class="cmp-banner__brand"><img src="${ENTAPRO_MARK_DATA_URI}" alt="EntaPro" class="cmp-banner__brand-mark" /><span>EntaPro</span></div>`
    : "";

  banner.innerHTML = `
    <div class="cmp-banner__content">
      ${noticeHtml}
      <h2 class="cmp-banner__title">${escapeHtml(translation.bannerTitle)}</h2>
      <p class="cmp-banner__desc">${escapeHtml(translation.bannerDescription)}</p>
      ${doNotSellHtml}
      <div class="cmp-banner__actions">
        <button class="cmp-btn cmp-btn--reject" id="cmp-reject-all">${escapeHtml(rejectLabel)}</button>
        <button class="cmp-btn cmp-btn--accept" id="cmp-accept-all">${escapeHtml(acceptLabel)}</button>
        <button class="cmp-btn cmp-btn--manage" id="cmp-manage">${escapeHtml(manageLabel)}</button>
      </div>
      ${brandHtml}
    </div>
  `;

  document.body.appendChild(banner);

  removeConsentWidget();

  const blockPage = options?.blockPage ?? shouldBlockPageUntilConsent(config.themeJson);
  if (blockPage) {
    setStrictPageBlock(true);
    acquirePageLock();
  }

  // Event listeners
  banner.querySelector("#cmp-reject-all")?.addEventListener("click", () => {
    onRejectAll();
    removeBanner();
  });
  banner.querySelector("#cmp-accept-all")?.addEventListener("click", () => {
    onAcceptAll();
    removeBanner();
  });
  banner.querySelector("#cmp-manage")?.addEventListener("click", () => {
    onManagePreferences();
  });
  
  if (isCCPA && onOptOut) {
    banner.querySelector("#cmp-do-not-sell")?.addEventListener("click", (e) => {
      e.preventDefault();
      onOptOut();
    });
  }

  return banner;
}

export function removeBanner(): void {
  document.getElementById("cmp-banner")?.remove();
  releasePageLockIfIdle();
  syncConsentWidget();
}

function escapeHtml(str: string): string {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}
